import { BookingProposalStatus, BookingProposalType } from '@navio/nsdk';
import { keepPreviousData } from '@tanstack/react-query';
import { noddiAsync, URLKeys } from 'noddi-async';
import { brands } from 'noddi-types';
import { useMemo } from 'react';
import { useCurrentBrand } from '../stores/BrandStore/brandStore';
import { isRecallProposalType } from '../utils/bookingProposalType';
import { useBrandCapabilities } from './useBrandCapabilities';

type Options = {
  pageIndex?: number;
  pageSize: number;
  /** Server-side search — matches license plate among other proposal fields. */
  search?: string;
  type?: BookingProposalType | null;
};

/** Every proposal type a recall can be published as, in the order the filter shows them. */
const PROPOSAL_TYPES = [
  BookingProposalType.RECALL_PROPOSAL_TIRE_HOTEL,
  BookingProposalType.RECALL_PROPOSAL_WHEEL_CHANGE,
  BookingProposalType.TIRE_CHANGE_AT_CUSTOMER,
  BookingProposalType.TIRE_CHANGE_AT_STORAGE_FACILITY
];

const NON_RECALL_PROPOSAL_TYPES = PROPOSAL_TYPES.filter((type) => !isRecallProposalType(type));

const pendingProposalsInput = (userGroupId: number) => ({
  userGroupIds: [userGroupId],
  statuses: [BookingProposalStatus.PENDING],
  isVisibleCustomer: true
});

/**
 * One page of the customer's pending proposals. `totalCount` is the server's count
 * for the same filters, so a page of ten can still say how many there are in all.
 */
export const usePendingBookingProposals = (userGroupId: number, { pageIndex = 0, pageSize, search, type }: Options) => {
  const { currentBrand } = useCurrentBrand();
  const { showsRecalls } = useBrandCapabilities();
  const { data, error, isPending, isFetching } = noddiAsync.useGet({
    type: URLKeys.getPaginatedBookingProposals,
    input: {
      ...pendingProposalsInput(userGroupId),
      pageIndex,
      pageSize,
      search: search || null,
      // Brands without recalls list every other type, not an unfiltered page recalls could fill.
      types: type ? [type] : showsRecalls ? undefined : NON_RECALL_PROPOSAL_TYPES
    },
    // Paging and typing keep the previous page on screen instead of collapsing the list.
    queryConfig: { placeholderData: keepPreviousData }
  });

  // The endpoint filters on brand ids while the app only knows the domain it is themed
  // by, so white-label brands are separated here, on the page that was fetched.
  const proposals = useMemo(
    () =>
      (data?.results ?? []).filter(
        (proposal) => currentBrand === brands.noddi || proposal.serviceDepartment.brand.domain === currentBrand
      ),
    [data?.results, currentBrand]
  );

  return { proposals, totalCount: data?.count ?? 0, error, isPending, isFetching };
};

/**
 * How many pending proposals the customer has of each type, for the filter counts.
 * One count request per type — the rows themselves are never fetched.
 */
export const usePendingProposalTypeCounts = (userGroupId: number) => {
  const { showsRecalls } = useBrandCapabilities();
  const visibleTypes = showsRecalls ? PROPOSAL_TYPES : NON_RECALL_PROPOSAL_TYPES;
  const queries = noddiAsync.useGetAll(
    visibleTypes.map((type) => ({
      type: URLKeys.getPaginatedBookingProposals,
      input: { ...pendingProposalsInput(userGroupId), types: [type], pageIndex: 0, pageSize: 1 }
    }))
  );

  const isPending = queries.some((query) => query.isPending);
  const counts = visibleTypes
    .map((type, index) => ({ type, count: queries[index]?.data?.count ?? 0 }))
    .filter(({ count }) => count > 0);

  return { typeCounts: counts, isPending };
};
